import { getCookie } from '../../../../utils/helpers';

export interface ProductHierarchyResponse {
  products: string[];
  categories: string[];
  productGroups: string[];
  customerAudiences: string[];
  audienceStrategyMappings?: Record<string, string[]>;
}

export interface DependentOptionsResponse {
  categories: string[];
  productGroups: string[];
  customerAudiences: string[];
  products: string[];
  audienceStrategyMappings?: Record<string, string[]>;
}

export const productHierarchyService = {
  // Simple in-memory caches to avoid duplicate requests
  _productsCache: null as string[] | null,
  _audiencesCache: null as string[] | null,
  _dependentOptionsCache: new Map<string, DependentOptionsResponse>(),
  _inflightDependentRequests: new Map<
    string,
    Promise<DependentOptionsResponse>
  >(),

  _normalizeCacheKey(
    selectedProducts: string | string[],
    selectedAudiences?: string | string[]
  ): string {
    const productsArr = Array.isArray(selectedProducts)
      ? selectedProducts.slice()
      : [selectedProducts];
    // Use sorted key to avoid cache misses due to order differences
    productsArr.sort((a, b) => a.localeCompare(b));

    // If audiences provided, include them in the cache key
    if (selectedAudiences) {
      const audiencesArr = Array.isArray(selectedAudiences)
        ? selectedAudiences.slice()
        : [selectedAudiences];
      audiencesArr.sort((a, b) => a.localeCompare(b));
      return JSON.stringify({ products: productsArr, audiences: audiencesArr });
    }

    return JSON.stringify({ products: productsArr });
  },

  async fetchProducts(): Promise<string[]> {
    const baseUrl =
      process.env.REACT_APP_API_V2_URL || '/product-roadmap/api/v2';
    const token = getCookie('app_access_token');
    if (this._productsCache) {
      return this._productsCache;
    }

    const response = await fetch(`${baseUrl}/product-hierarchy-filters`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({})
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch products: ${response.status}`);
    }

    const data: ProductHierarchyResponse = await response.json();
    this._productsCache = data.products || [];
    return this._productsCache;
  },

  async fetchAllAudiences(): Promise<string[]> {
    const baseUrl =
      process.env.REACT_APP_API_V2_URL || '/product-roadmap/api/v2';
    const token = getCookie('app_access_token');
    if (this._audiencesCache) {
      return this._audiencesCache;
    }

    const response = await fetch(`${baseUrl}/product-hierarchy-filters`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({})
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch audiences: ${response.status}`);
    }

    const data: ProductHierarchyResponse = await response.json();
    this._audiencesCache = data.customerAudiences || [];
    return this._audiencesCache;
  },

  async fetchDependentOptions(
    selectedProducts: string | string[],
    selectedAudiences?: string | string[]
  ): Promise<DependentOptionsResponse> {
    const baseUrl =
      process.env.REACT_APP_API_V2_URL || '/product-roadmap/api/v2';
    const token = getCookie('app_access_token');
    const key = this._normalizeCacheKey(selectedProducts, selectedAudiences);
    // Serve from cache if available
    if (this._dependentOptionsCache.has(key)) {
      return this._dependentOptionsCache.get(key)!;
    }
    // De-duplicate concurrent requests
    if (this._inflightDependentRequests.has(key)) {
      return this._inflightDependentRequests.get(key)!;
    }

    const requestPromise = (async () => {
      // Build request body
      const requestBody: any = {
        products: Array.isArray(selectedProducts)
          ? selectedProducts
          : [selectedProducts]
      };

      // Include customerAudiences if provided
      if (selectedAudiences) {
        requestBody.customerAudiences = Array.isArray(selectedAudiences)
          ? selectedAudiences
          : [selectedAudiences];
      }

      const response = await fetch(`${baseUrl}/product-hierarchy-filters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch dependent options: ${response.status}`
        );
      }

      const data: DependentOptionsResponse = await response.json();
      const normalized: DependentOptionsResponse = {
        categories: data.categories || [],
        productGroups: data.productGroups || [],
        customerAudiences: data.customerAudiences || [],
        products: data.products || [],
        audienceStrategyMappings: data.audienceStrategyMappings || {}
      };
      // Store in cache and return
      this._dependentOptionsCache.set(key, normalized);
      return normalized;
    })();

    // Track inflight, then cleanup
    this._inflightDependentRequests.set(key, requestPromise);
    try {
      const result = await requestPromise;
      return result;
    } finally {
      this._inflightDependentRequests.delete(key);
    }
  }
};
